import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import HotplaceDetailPanel from '@/components/feature/list-type/hotplace/hotplace-detail-panel';
import HotplaceItemListBar from '@/components/feature/list-type/hotplace/hotplace-item-list-bar';
import { FloorGuideType } from '@/enums/floor-guide';
import { useCurrentCustomMenu } from '@/hooks/custom-menu';
import { useOrientation } from '@/hooks/useOrientation';
import type { BuildingMenu } from '@/services/dto';
import { PATHS } from '@/router/paths';
import { useConfigStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import type { ListTypeListItem } from '@/types/list-type';
import { findPoiContentInBuildings } from '@/utils/poi/enrichPoiContent';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './HotplaceListTypePage.module.scss';

interface HotplaceListTypePageProps {
    gnbMenuId?: string;
}

const HotplaceListTypePage = ({ gnbMenuId }: HotplaceListTypePageProps) => {
    const { t } = useTranslation();
    const ui = useConfigStore((state) => state.config.ui);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const { isHorizontal } = useOrientation();
    const navigate = useNavigate();
    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();
    const { currentMenu, customContent } = useCurrentCustomMenu(gnbMenuId);

    const [selectedItemId, setSelectedItemId] = useState<string>('');

    useEffect(() => {
        if (customContent.length === 0) {
            setSelectedItemId('');
            return;
        }

        setSelectedItemId((prev) => {
            if (prev && customContent.some((item) => item.id === prev)) {
                return prev;
            }
            return customContent[0].id;
        });
    }, [customContent]);

    const selectedItem = useMemo(
        () => customContent.find((item) => item.id === selectedItemId) ?? null,
        [customContent, selectedItemId]
    );

    const resolvePoiBuildingId = useCallback(
        (poiId: string) => findPoiContentInBuildings(poiId, buildings ?? [])?.building.studioBuildingId,
        [buildings]
    );

    const handleLocationCheck = useCallback(
        (detail: ListTypeListItem) => {
            if (!detail.poiId) return;

            setSelectedPoi({
                studioFloorID: detail.floorId,
                studioPoiId: detail.poiId,
                buildingId: resolvePoiBuildingId(detail.poiId),
            });
            setFloorGuideType(FloorGuideType.MARKER);
            navigate(PATHS.FLOOR_GUIDE.CURRENT_LOCATION(detail.floorId, detail.poiId));
        },
        [navigate, resolvePoiBuildingId, setFloorGuideType, setSelectedPoi]
    );

    const handleDirection = useCallback(
        (detail: ListTypeListItem) => {
            if (!detail.poiId) return;

            setSelectedPoi({
                studioFloorID: detail.floorId,
                studioPoiId: detail.poiId,
                buildingId: resolvePoiBuildingId(detail.poiId),
            });
            setFloorGuideType(FloorGuideType.DIRECTION);
            navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(detail.floorId, detail.poiId));
        },
        [navigate, resolvePoiBuildingId, setFloorGuideType, setSelectedPoi]
    );

    const pageClassname = useMemo(
        () =>
            clsx(styles.hotplace, {
                [styles['hotplace--vertical']]: !isHorizontal,
                [styles['hotplace--horizontal']]: isHorizontal,
                [styles['hotplace--horizontal--halfmode']]: ui.screenMode === 'split' && isHorizontal,
                [styles['hotplace--vertical--halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            }),
        [ui.screenMode, isHorizontal]
    );

    const menuName = (currentMenu?.name as string) || '';
    const menuDescription = (currentMenu as BuildingMenu | undefined)?.description || '';

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['hotplace__header'], {
                    [styles['hotplace__header--horizontal']]: isHorizontal,
                    [styles['hotplace__header--vertical']]: !isHorizontal,
                    [styles['hotplace__header--vertical--halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader title={menuName} description={menuDescription} isHorizontal={isHorizontal} tabIndex={1} />
            </div>

            <div className={styles['hotplace__body']}>
                {selectedItem ? (
                    <HotplaceDetailPanel
                        item={selectedItem}
                        onLocationClick={() => handleLocationCheck(selectedItem)}
                        onDirectionClick={() => handleDirection(selectedItem)}
                    />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="hotplace-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>

            {customContent.length > 0 && (
                <HotplaceItemListBar
                    items={customContent}
                    selectedId={selectedItemId}
                    onSelect={setSelectedItemId}
                    isHorizontal={isHorizontal}
                    halfMode={ui.screenMode === 'split'}
                    tabIndexBase={8}
                />
            )}
        </div>
    );
};

export default HotplaceListTypePage;
